import imageCompression from "browser-image-compression";

const OUTPUT_TYPE = "image/webp" as const;
const RASTER_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/bmp",
] as const;

function isRasterImage(type: string): type is (typeof RASTER_TYPES)[number] {
  return RASTER_TYPES.includes(type as (typeof RASTER_TYPES)[number]);
}

const COMPRESSION_OPTIONS = {
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1200,
  useWebWorker: true,
} as const;

/**
 * Returns a clean filename for the compressed output (timestamp + extension) to avoid
 * names like "img_webp.webp" when the original had a weird name.
 */
function compressedFileName(mimeType: string): string {
  const ext = mimeType === "image/webp" ? ".webp" : ".jpg";
  return `${Date.now()}${ext}`;
}

/**
 * Compresses an image file for upload: max 1200px width/height, max 500KB, output WebP.
 * SVG is returned unchanged (pass-through). Non-image or unsupported types throw.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  const type = (file.type ?? "").toLowerCase().trim();

  if (type === "image/svg+xml") {
    return file;
  }

  if (!type || !isRasterImage(type)) {
    throw new Error(
      "El archivo no es una imagen válida. Use JPEG, PNG, WebP o GIF.",
    );
  }

  try {
    const compressed = await imageCompression(file, {
      ...COMPRESSION_OPTIONS,
      fileType: OUTPUT_TYPE,
    });

    const name = compressedFileName(OUTPUT_TYPE);
    return new File([compressed], name, { type: OUTPUT_TYPE });
  } catch {
    throw new Error("No se pudo procesar la imagen. Pruebe con otro archivo.");
  }
}

const OUTPUT_TYPE_JPEG = "image/jpeg" as const;

export interface CompressDualResult {
  webp: File;
  jpeg: File;
}

/**
 * Compresses an image to both WebP (for frontend) and JPEG (for email clients).
 * Same size limits as compressImageForUpload. SVG is returned unchanged for both.
 */
export async function compressImageForUploadDual(
  file: File,
): Promise<CompressDualResult> {
  const type = (file.type ?? "").toLowerCase().trim();

  if (type === "image/svg+xml") {
    return { webp: file, jpeg: file };
  }

  if (!type || !isRasterImage(type)) {
    throw new Error(
      "El archivo no es una imagen válida. Use JPEG, PNG, WebP o GIF.",
    );
  }

  try {
    const [compressedWebp, compressedJpeg] = await Promise.all([
      imageCompression(file, {
        ...COMPRESSION_OPTIONS,
        fileType: OUTPUT_TYPE,
      }),
      imageCompression(file, {
        ...COMPRESSION_OPTIONS,
        fileType: OUTPUT_TYPE_JPEG,
      }),
    ]);

    const nameWebp = compressedFileName(OUTPUT_TYPE);
    const nameJpeg = compressedFileName(OUTPUT_TYPE_JPEG);
    return {
      webp: new File([compressedWebp], nameWebp, { type: OUTPUT_TYPE }),
      jpeg: new File([compressedJpeg], nameJpeg, { type: OUTPUT_TYPE_JPEG }),
    };
  } catch {
    throw new Error("No se pudo procesar la imagen. Pruebe con otro archivo.");
  }
}
